import { Listbox } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'
import { ReactNode } from 'react'

import { ScreenSize } from '@/app/constants'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'

import styles from './AppSelect.module.css'

export type AppSelectOption = {
  id: string | number
  name: string
  icon?: ReactNode
  isDisabled?: boolean
}

type Props = {
  label?: string
  className?: string
  options?: AppSelectOption[]
  selectedOption?: AppSelectOption
  onChange?: (option: AppSelectOption) => unknown
  disabled?: boolean
  isSpecialSelectedText?: boolean
  additionalImage?: ReactNode
  isFontBold?: boolean
  maxOptionsHeight?: number
  isError?: boolean
}

export const AppSelect = ({
  label,
  className,
  options,
  selectedOption,
  isSpecialSelectedText,
  onChange,
  isFontBold,
  additionalImage,
  disabled,
  maxOptionsHeight = 224,
  isError
}: Props) => {
  const { breakpoint } = useCurrentBreakpoint()
  const isMobileScreen = [ScreenSize.Xxs, ScreenSize.Xs].includes(breakpoint)

  const shouldShowScroll = options && options.length > 5
  const fontWeight = isFontBold ? 'font-bold' : ''

  const getLabelPosition = (open: boolean) => {
    const isSelected = open || selectedOption
    return label && isSelected
      ? 'top-5 translate-y-0 text-12'
      : 'top-20 -translate-y-1/2 text-12'
  }

  return (
    <Listbox
      disabled={disabled}
      data-component="AppSelect"
      by="id"
      as="div"
      className={cn('relative min-w-[130px]', className)}
      value={selectedOption}
      onChange={onChange}
    >
      {({ open }) => (
        <div
          className={cn(
            'h-full w-full rounded-12 border border-grey-20 bg-white',
            open &&
              'h-auto rounded-b-0 border-b-transparent shadow-[0px_7px_10px_0px_rgba(22,23,24,0.10)]',
            isError && 'border-red-80'
          )}
        >
          <Listbox.Button
            className={cn(
              'relative flex h-56 w-full space-x-12 px-12 py-7 text-start',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            <span
              className={cn('flex h-full w-full flex-col justify-end', {
                'justify-center': !label
              })}
            >
              <div
                className={cn(
                  'absolute font-pro opacity-50 transition-all',
                  isSpecialSelectedText
                    ? cn(
                        fontWeight,
                        'left-0 top-1/2 flex h-[52%] -translate-y-1/2 flex-row gap-4 font-bold text-black/50 text-12'
                      )
                    : cn('left-12', getLabelPosition(open))
                )}
              >
                {additionalImage ?? null}
                {label}
              </div>

              <span
                className={cn(
                  'truncate pr-32 font-pro text-grey-100 text-16',
                  isFontBold && isMobileScreen ? 'font-bold' : 'not-empty'
                )}
              >
                {selectedOption?.name}
              </span>
            </span>

            <span className="pointer-events-none absolute inset-y-0 right-16 flex items-center pr-2">
              <ChevronDownIcon
                className={cn('h-30 w-30 text-grey-40', {
                  '-scale-y-100': open,
                  'text-grey-40': disabled
                })}
              />
            </span>
            {open && (
              <div className="absolute -bottom-1 left-4 right-24 block h-1 bg-grey-20"></div>
            )}
          </Listbox.Button>

          <Listbox.Options
            className={cn(
              'absolute -right-1 left-0 top-full z-50 w-full overflow-hidden rounded-b-12 border border-t-0 border-grey-20',
              {
                'shadow-[0px_7px_10px_0px_rgba(22,23,24,0.10)]': open
              }
            )}
          >
            <div
              className={cn(
                'overflow-y-auto',
                shouldShowScroll && 'max-h-[224px]',
                styles.customScroll
              )}
              style={
                shouldShowScroll ? { maxHeight: maxOptionsHeight } : undefined
              }
            >
              {options?.map(option => (
                <Listbox.Option
                  key={option.id}
                  className={({ active }) =>
                    cn(
                      'relative flex items-center px-16 py-12',
                      option.isDisabled
                        ? 'cursor-not-allowed bg-grey-10 text-grey-40'
                        : 'cursor-pointer select-none bg-white',
                      active && !option.isDisabled && 'bg-grey-10'
                    )
                  }
                  value={option}
                  disabled={option.isDisabled}
                >
                  {({ selected }) => (
                    <div className="flex w-full items-center justify-between">
                      <div className="flex items-center gap-4">
                        {option.icon || null}
                        <span
                          className={clsx(
                            option.isDisabled
                              ? 'text-grey-40'
                              : 'text-grey-90 text-16',
                            selected && !option.isDisabled && '!text-bronze-40'
                          )}
                        >
                          {option.name}
                        </span>
                      </div>
                      {selected && !option.isDisabled && (
                        <CheckIcon className="w-20 text-bronze-40" />
                      )}
                    </div>
                  )}
                </Listbox.Option>
              )) ?? []}
            </div>
          </Listbox.Options>
        </div>
      )}
    </Listbox>
  )
}
